import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MediaCard from "@/components/feature/MediaCard";
import EmptyState from "@/components/feature/EmptyState";
import { getSpecialById, getSpecialMedia, specials } from "@/utils/media";

export default function SpecialPage() {
  const { id } = useParams();
  const special = getSpecialById(id ?? "");
  const items = useMemo(() => (special ? getSpecialMedia(special) : []), [special]);
  const others = specials.filter((item) => item.id !== id);

  useDocumentMeta({
    title: special
      ? `${special.title} - 专题合集 - 亚洲一区二区在线`
      : "专题不存在 - 亚洲一区二区在线",
    description: special
      ? `${special.subtitle}。${special.intro.slice(0, 60)}…`
      : "未找到对应的影视专题。",
    keywords: special
      ? `${special.title},影视专题,亚洲影视合集,亚洲一区二区在线`
      : "亚洲一区二区在线,影视专题",
  });

  if (!special) {
    return (
      <div className="mx-auto w-full px-4 py-16 md:px-6">
        <EmptyState
          icon="ri-bookmark-line"
          title="该专题不存在或已下线"
          description="你访问的影视专题可能已被调整，返回首页看看其他精彩专题吧。"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  return (
    <div className="pb-4">
      <section className="relative w-full overflow-hidden">
        <div className="relative h-[280px] w-full md:h-[360px]">
          <img
            src={special.cover}
            alt={special.title}
            title={`${special.title} 专题封面`}
            className="h-full w-full object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/95 via-foreground-950/60 to-foreground-950/20"></div>
        </div>
        <div className="absolute inset-0">
          <div className="mx-auto flex h-full w-full flex-col justify-end px-4 pb-8 md:px-6 md:pb-10">
            <Breadcrumb items={[{ label: "精选专题" }, { label: special.title }]} />
            <h1 className="mt-3 font-heading text-2xl font-bold text-background-50 md:text-4xl">
              {special.title}
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-background-300 md:text-base">
              {special.subtitle}
            </p>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full px-4 md:px-6">
        <div className="mt-8 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
            <i className="ri-quill-pen-line text-primary-500"></i>
            专题导语
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-700">{special.intro}</p>
          <div className="mt-4 flex items-start gap-2 rounded-md bg-accent-50 px-4 py-3 text-sm text-accent-700">
            <i className="ri-lightbulb-line mt-0.5"></i>
            <p>
              <strong className="font-semibold">推荐理由：</strong>
              {special.reason}
            </p>
          </div>
        </div>

        <section className="mt-10">
          <h2 className="mb-5 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            <i className="ri-stack-line text-accent-500"></i>
            精选影视作品
          </h2>
          <div className="grid grid-cols-2 gap-3 md:gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {items.map((media) => (
              <MediaCard key={media.id} media={media} showSynopsis={false} />
            ))}
          </div>
        </section>

        <section className="mt-12">
          <h2 className="mb-5 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            <i className="ri-bookmark-3-line text-primary-500"></i>
            相关专题
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {others.map((item) => (
              <Link
                key={item.id}
                to={`/special/${item.id}`}
                className="group flex items-center gap-4 overflow-hidden rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
              >
                <span className="h-20 w-32 shrink-0 overflow-hidden rounded-md bg-background-200">
                  <img
                    src={item.cover}
                    alt={item.title}
                    title={`${item.title} 专题封面`}
                    className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </span>
                <span className="min-w-0">
                  <span className="block font-heading text-base font-bold text-foreground-950 group-hover:text-primary-600">
                    {item.title}
                  </span>
                  <span className="mt-1 line-clamp-1 block text-xs text-foreground-500">
                    {item.subtitle}
                  </span>
                  <span className="mt-2 inline-flex items-center gap-1 text-xs text-primary-600">
                    查看专题
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </span>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}